import { useEffect, useState } from "react";
import { DebugLog, type LogEntry } from "../debug/log";
import { effectiveModelConfig, type Settings } from "../data/types";
import { isOpfsAvailable, probeModelMagic, type ModelProbe } from "../data/modelStore";
import { describeWebGpu, isWebGpuAvailable, MEDIAPIPE_VERSION } from "../engine/localEngine";
import { formatBytes } from "../ui/format";

interface Props {
  settings: Settings;
  onBack: () => void;
}

interface SelfCheck {
  mediapipeVersion: string;
  webgpuAvailable: boolean;
  webgpuDescription: string;
  opfsAvailable: boolean;
  modelFileName: string;
  modelProbe: ModelProbe;
}

export function DebugScreen(props: Props) {
  const [entries, setEntries] = useState<LogEntry[]>(() => DebugLog.snapshot());
  const [self, setSelf] = useState<SelfCheck | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    return DebugLog.subscribe(() => setEntries(DebugLog.snapshot()));
  }, []);

  const runSelfCheck = async () => {
    setRefreshing(true);
    const fileName = effectiveModelConfig(props.settings).fileName;
    const [opfs, gpuDesc, probe] = await Promise.all([
      isOpfsAvailable(),
      describeWebGpu(),
      probeModelMagic(fileName),
    ]);
    setSelf({
      mediapipeVersion: MEDIAPIPE_VERSION,
      webgpuAvailable: isWebGpuAvailable(),
      webgpuDescription: gpuDesc,
      opfsAvailable: opfs,
      modelFileName: fileName,
      modelProbe: probe,
    });
    setRefreshing(false);
  };

  useEffect(() => {
    void runSelfCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyAll = async () => {
    const header = self
      ? [
          "# Self-check",
          `mediapipe.version: ${self.mediapipeVersion}`,
          `webgpu.available: ${self.webgpuAvailable}`,
          `webgpu.adapter: ${self.webgpuDescription}`,
          `opfs.available: ${self.opfsAvailable}`,
          `model.fileName: ${self.modelFileName}`,
          `model.exists: ${self.modelProbe.exists}`,
          `model.sizeBytes: ${self.modelProbe.sizeBytes ?? "null"}`,
          `model.magicHex: ${self.modelProbe.magicHex ?? "null"}`,
          `model.magicAscii: ${self.modelProbe.magicAscii ?? "null"}`,
          "",
          "# Log",
        ].join("\n")
      : "# Log";
    const text = `${header}\n${DebugLog.toPlainText()}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback: open a prompt the user can long-press to copy on mobile
      // when clipboard API is unavailable (some embedded webviews).
      window.prompt("Copy debug output:", text);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
        <h1 className="text-lg font-semibold tracking-tight">Debug</h1>
        <div className="flex items-center gap-1">
          <button className="btn-ghost" onClick={copyAll}>
            Copy
          </button>
          <button
            className="btn-ghost"
            onClick={() => {
              DebugLog.clear();
            }}
          >
            Clear
          </button>
        </div>
      </header>

      <main className="flex flex-1 flex-col gap-5 px-4 py-4">
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">Self-check</h2>
            <button className="btn-ghost" disabled={refreshing} onClick={runSelfCheck}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          <SelfCheckCard data={self} />
        </section>

        <section className="flex flex-col gap-2">
          <h2 className="text-base font-semibold">Log ({entries.length})</h2>
          {entries.length === 0 ? (
            <p className="rounded-xl bg-surface p-3 text-sm text-ink-muted ring-1 ring-black/5">
              Empty. Trigger something — install the model or tap Test connection — and refresh.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {entries
                .slice()
                .reverse()
                .map((entry, idx) => (
                  <li key={`${entry.ts}-${idx}`}>
                    <LogRow entry={entry} />
                  </li>
                ))}
            </ul>
          )}
        </section>
      </main>
    </>
  );
}

function SelfCheckCard(props: { data: SelfCheck | null }) {
  if (!props.data) {
    return (
      <div className="rounded-xl bg-surface p-3 text-sm text-ink-muted ring-1 ring-black/5">
        Loading…
      </div>
    );
  }
  const d = props.data;
  return (
    <div className="rounded-xl bg-surface p-3 text-sm ring-1 ring-black/5">
      <KV k="mediapipe" v={d.mediapipeVersion} />
      <KV k="webgpu" v={d.webgpuAvailable ? "available" : "missing"} tone={d.webgpuAvailable ? "ok" : "bad"} />
      <KV k="webgpu adapter" v={d.webgpuDescription} mono />
      <KV k="opfs" v={d.opfsAvailable ? "available" : "missing"} tone={d.opfsAvailable ? "ok" : "bad"} />
      <KV k="model file" v={d.modelFileName} mono />
      <KV
        k="model present"
        v={d.modelProbe.exists ? `yes (${formatBytes(d.modelProbe.sizeBytes ?? 0)})` : "no"}
        tone={d.modelProbe.exists ? "ok" : "warn"}
      />
      {d.modelProbe.exists && (
        <>
          <KV k="magic (hex)" v={d.modelProbe.magicHex ?? "?"} mono />
          <KV k="magic (ascii)" v={d.modelProbe.magicAscii ?? "?"} mono />
          <p className="mt-2 text-xs text-ink-muted">
            <strong>Expected magic prefixes:</strong> LiteRT-LM files typically start with bytes
            that include "LITERT" or "LRT" near the head. ZIP-based .task files start with{" "}
            <code className="text-xs">PK..</code>. Raw TFLite begins with{" "}
            <code className="text-xs">....TFL3</code>. HTML (the failure mode where the URL pointed
            at a repo, not a file) starts with{" "}
            <code className="text-xs">&lt;!DOCTYPE</code> or <code className="text-xs">&lt;html</code>.
          </p>
        </>
      )}
    </div>
  );
}

function KV(props: { k: string; v: string; tone?: "ok" | "warn" | "bad"; mono?: boolean }) {
  const valueClass =
    props.tone === "ok"
      ? "text-emerald-600"
      : props.tone === "bad"
        ? "text-rose-600"
        : props.tone === "warn"
          ? "text-amber-600"
          : "text-ink";
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0 py-0.5">
      <span className="text-xs font-semibold text-ink-muted">{props.k}</span>
      <span
        className={`text-xs ${props.mono ? "font-mono" : ""} ${valueClass} break-all`}
      >
        {props.v}
      </span>
    </div>
  );
}

function LogRow(props: { entry: LogEntry }) {
  const e = props.entry;
  const tone =
    e.level === "error"
      ? "bg-rose-50 ring-rose-200 text-rose-900"
      : e.level === "warn"
        ? "bg-amber-50 ring-amber-200 text-amber-900"
        : e.level === "info"
          ? "bg-accent-soft ring-accent/20 text-ink"
          : "bg-surface ring-black/5 text-ink";
  return (
    <div className={`rounded-xl p-3 text-xs ring-1 ${tone}`}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-mono text-[10px] text-ink-muted">
          {new Date(e.ts).toISOString().slice(11, 23)}
        </span>
        <span className="font-semibold uppercase tracking-wide text-[10px]">{e.level}</span>
        <span className="text-[10px] font-semibold text-ink-muted">{e.tag}</span>
      </div>
      <div className="mt-1 break-words">{e.message}</div>
      {e.detail && (
        <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] text-ink-muted">
          {e.detail}
        </pre>
      )}
    </div>
  );
}
